import type { WorkingPaperData, WorkingPaperQueryResponse } from '@/services/auditService/interface/working-paper';
import {
  projectYoaBucketToWorkingPaperData,
  setWpYoaMapCache,
  type WpYoaBucket,
} from '@/services/auditService/wpYoaMapCache';
import { getActiveWpYearOfAssessment } from '@/services/auditService/wpPeriodYoaBridge';
import { hydrateWpSignoffFromPayload } from '@/store/audit/wpSignoffStore';

const YOA_KEY_RE = /^\d{4}\/\d{2}$/;
const IC_CODE = 'BS-EQ-IC';
/** PL-TURN-SI/SE MULTI：与 YOA bucket 同级的共享键（不进 byYearOfAssessment 缓存） */
const SHARED_YOA_MAP_KEYS = new Set(['annualInfo']);

/**
 * MULTI：{@code details} 为 YOA → bucket Map（原 byYearOfAssessment 形态）。
 * 允许同级共享键（如 annualInfo），但至少须有一个 YOA key。
 */
export function isYoaDetailsMap(details: unknown): details is Record<string, WpYoaBucket> {
  if (!details || typeof details !== 'object' || Array.isArray(details)) return false;
  const keys = Object.keys(details as Record<string, unknown>);
  if (keys.length === 0) return false;
  const yoaKeys = keys.filter((k) => YOA_KEY_RE.test(k));
  if (yoaKeys.length === 0) return false;
  return keys.every((k) => YOA_KEY_RE.test(k) || SHARED_YOA_MAP_KEYS.has(k));
}

/** shareholders field is YOA → list map */
export function isShareholdersYoaMap(shareholders: unknown): shareholders is Record<string, any[]> {
  if (!shareholders || typeof shareholders !== 'object' || Array.isArray(shareholders)) return false;
  const keys = Object.keys(shareholders as Record<string, unknown>);
  if (keys.length === 0) return false;
  return keys.every((k) => YOA_KEY_RE.test(k));
}

/**
 * BS-EQ-IC MULTI wire bag:
 * { shareholders: { "2010/11": [...] }, registeredInfo, directors, holdingCompany }
 */
export function isIssuedCapitalInvertedBag(bag: unknown): bag is {
  shareholders: Record<string, any[]>;
  registeredInfo?: any;
  directors?: any;
  holdingCompany?: any;
  adjustments?: any;
  [key: string]: any;
} {
  if (!bag || typeof bag !== 'object' || Array.isArray(bag)) return false;
  return isShareholdersYoaMap((bag as any).shareholders);
}

/**
 * Extract IC inverted bag from GET details:
 * - details: [ bag ]  (preferred MULTI wire)
 * - details: bag       (legacy flat inverted)
 */
export function extractIssuedCapitalInvertedBag(details: unknown): {
  shareholders: Record<string, any[]>;
  registeredInfo?: any;
  directors?: any;
  holdingCompany?: any;
  adjustments?: any;
  [key: string]: any;
} | null {
  if (Array.isArray(details) && details.length > 0 && isIssuedCapitalInvertedBag(details[0])) {
    return details[0];
  }
  if (isIssuedCapitalInvertedBag(details)) {
    return details;
  }
  return null;
}

/**
 * 将 Facade 查询响应合并为组件沿用的 WorkingPaperData[] 格式。
 *
 * #20 SINGLE：{@code details} 为数组且 shareholders 为 List → {@link mergeFlatResponse}。
 * MULTI 其它 WP：{@code spanType=MULTI} 且 {@code details} 为 YOA Map。
 * BS-EQ-IC MULTI：{@code spanType=MULTI} + {@code details:[{ shareholders: {yoa→[]} , ... }]}.
 */
export function mergeWorkingPaperQueryResponse(
  response: WorkingPaperQueryResponse | null | undefined,
  opts?: {
    companyId?: number;
    annualId?: number;
    code?: string;
    activeYoa?: string | null;
  },
): WorkingPaperData[] {
  if (!response || typeof response !== 'object') {
    return [];
  }

  const span = String(response.spanType || '').toUpperCase();
  const code = String(opts?.code || '').trim().toUpperCase();

  // BS-EQ-IC MULTI only: spanType=MULTI + details=[{ shareholders: {yoa→[]}, ... }]
  // SINGLE (no spanType / shareholders is List) never enters here.
  if (span === 'MULTI' && code === IC_CODE) {
    const inverted = extractIssuedCapitalInvertedBag(response.details);
    if (inverted) {
      return mergeIssuedCapitalInverted(response, inverted, opts);
    }
  }

  // MULTI 新契约：仅当明确 MULTI 且 details 为 YOA Map（不误伤 SINGLE List）
  if (span === 'MULTI' && isYoaDetailsMap(response.details)) {
    return mergeYoaMapFromDetails(response, response.details, opts);
  }

  // 兼容旧 byYearOfAssessment（仅 MULTI 白名单历史响应）
  if (
    span === 'MULTI'
    && response.byYearOfAssessment
    && typeof response.byYearOfAssessment === 'object'
  ) {
    return mergeYoaMapFromDetails(
      response,
      response.byYearOfAssessment as Record<string, WpYoaBucket>,
      opts,
    );
  }

  // SINGLE / 无 spanType / details=List → 现网扁平合并
  return mergeFlatResponse(response, opts);
}

/** Convert IC inverted bag → internal YOA bucket cache, then project active slice. */
function mergeIssuedCapitalInverted(
  response: WorkingPaperQueryResponse,
  inverted: {
    shareholders: Record<string, any[]>;
    registeredInfo?: any;
    directors?: any;
    holdingCompany?: any;
    adjustments?: any;
    [key: string]: any;
  },
  opts?: {
    companyId?: number;
    annualId?: number;
    code?: string;
    activeYoa?: string | null;
  },
): WorkingPaperData[] {
  const yoaOrder = Object.keys(inverted.shareholders);
  const byYoa: Record<string, WpYoaBucket> = {};
  for (const yoa of yoaOrder) {
    byYoa[yoa] = {
      details: [{
        shareholders: Array.isArray(inverted.shareholders[yoa])
          ? inverted.shareholders[yoa]
          : [],
        registeredInfo: inverted.registeredInfo ?? {},
        directors: Array.isArray(inverted.directors) ? inverted.directors : [],
        holdingCompany: inverted.holdingCompany ?? {},
        ...(Array.isArray(inverted.adjustments) ? {adjustments: inverted.adjustments} : {}),
      }],
    };
  }
  // Legacy IC UI forces dataMode=single (clears bridge YOA). Transfers stamp annual/meta YOA
  // (usually last engagement slice). Prefer explicit/bridge YOA; else last key — not order[0].
  const bridgeOrExplicit = (opts?.activeYoa && String(opts.activeYoa).trim())
    || getActiveWpYearOfAssessment();
  const fallbackYoa = yoaOrder.length > 0 ? yoaOrder[yoaOrder.length - 1] : '';
  return mergeYoaMapFromDetails(response, byYoa, {
    ...opts,
    activeYoa: bridgeOrExplicit || fallbackYoa || null,
  });
}

function mergeYoaMapFromDetails(
  response: WorkingPaperQueryResponse,
  byYoa: Record<string, WpYoaBucket>,
  opts?: {
    companyId?: number;
    annualId?: number;
    code?: string;
    activeYoa?: string | null;
  },
): WorkingPaperData[] {
  // PL-TURN-SI/SE：顶层 annualInfo 与 YOA bucket 同级，缓存只收 YOA keys
  const rawMap = byYoa as Record<string, unknown>;
  const sharedAnnualInfo = rawMap.annualInfo;
  const yoaOrder = Object.keys(byYoa).filter((k) => YOA_KEY_RE.test(k));
  const bucketsOnly: Record<string, WpYoaBucket> = {};
  for (const yoa of yoaOrder) {
    bucketsOnly[yoa] = byYoa[yoa] || {};
  }
  const companyId = opts?.companyId ?? 0;
  const annualId = opts?.annualId ?? 0;
  const code = opts?.code || '';

  if (companyId > 0 && annualId > 0 && code) {
    const normalized: Record<string, WpYoaBucket> = {};
    for (const yoa of yoaOrder) {
      const bucket = bucketsOnly[yoa] || {};
      normalized[yoa] = {
        details: bucket.details,
        glInvoiceDetails: bucket.glInvoiceDetails ?? null,
        preparedBy: bucket.preparedBy ?? null,
        reviewedBy: bucket.reviewedBy ?? null,
        preparedDate: bucket.preparedDate ?? null,
        reviewedDate: bucket.reviewedDate ?? null,
        preparedUserIds: bucket.preparedUserIds ?? null,
        reviewedUserIds: bucket.reviewedUserIds ?? null,
      };
    }
    setWpYoaMapCache({
      companyId,
      annualId,
      code,
      byYearOfAssessment: normalized,
      yoaOrder,
      annualInfo: (sharedAnnualInfo != null && typeof sharedAnnualInfo === 'object')
        ? sharedAnnualInfo as Record<string, unknown>
        : null,
      wpStatus: response.wpStatus,
      wpIssueRemark: response.wpIssueRemark,
      outstanding: response.outstanding,
      outstandingRemark: response.outstandingRemark,
      issueStatus: response.issueStatus,
    });
  }

  const activeYoa = opts?.activeYoa || getActiveWpYearOfAssessment();
  const entry = {
    companyId,
    annualId,
    code,
    byYearOfAssessment: bucketsOnly,
    yoaOrder,
    annualInfo: (sharedAnnualInfo != null && typeof sharedAnnualInfo === 'object')
      ? sharedAnnualInfo as Record<string, unknown>
      : null,
    wpStatus: response.wpStatus,
    wpIssueRemark: response.wpIssueRemark,
    outstanding: response.outstanding,
    outstandingRemark: response.outstandingRemark,
    issueStatus: response.issueStatus,
  };
  const projected = projectYoaBucketToWorkingPaperData(entry, activeYoa);

  if (Array.isArray(response.autoAdjustments) && response.autoAdjustments.length > 0) {
    projected.autoAdjustments = response.autoAdjustments;
  }
  if (response.smeFrs != null) {
    projected['SME-FRS'] = response.smeFrs;
  }
  if (response.wokeDone != null) {
    projected['WOKE_DONE'] = response.wokeDone;
  }
  if (projected.glInvoiceDetails == null && response.glInvoiceDetails != null) {
    projected.glInvoiceDetails = response.glInvoiceDetails;
  }
  if (response.spanType != null) {
    projected.spanType = response.spanType;
  }
  if (companyId > 0 && annualId > 0 && code) {
    hydrateWpSignoffFromPayload(
      { companyId, annualId, code, yearOfAssessment: activeYoa || '' },
      projected as Record<string, unknown>,
    );
  }

  return [projected as WorkingPaperData];
}

function mergeFlatResponse(
  response: WorkingPaperQueryResponse,
  opts?: {
    companyId?: number;
    annualId?: number;
    code?: string;
    activeYoa?: string | null;
  },
): WorkingPaperData[] {
  const rawDetails = response.details;
  const details = Array.isArray(rawDetails) ? rawDetails : [];
  const metadata: Record<string, unknown> = {};

  if (Array.isArray(response.autoAdjustments) && response.autoAdjustments.length > 0) {
    metadata.autoAdjustments = response.autoAdjustments;
  }
  if (response.smeFrs != null) {
    metadata['SME-FRS'] = response.smeFrs;
  }
  if (response.wokeDone != null) {
    metadata['WOKE_DONE'] = response.wokeDone;
  }
  if (response.glInvoiceDetails != null) {
    metadata.glInvoiceDetails = response.glInvoiceDetails;
  }
  if (response.wpStatus != null) {
    metadata.wpStatus = response.wpStatus;
  }
  if (response.wpIssueRemark != null) {
    metadata.wpIssueRemark = response.wpIssueRemark;
  }
  if (response.outstanding != null) {
    metadata.outstanding = response.outstanding;
  }
  if (response.outstandingRemark != null) {
    metadata.outstandingRemark = response.outstandingRemark;
  }
  if (response.issueStatus != null) {
    metadata.issueStatus = response.issueStatus;
  }
  if (response.preparedBy != null) metadata.preparedBy = response.preparedBy;
  if (response.reviewedBy != null) metadata.reviewedBy = response.reviewedBy;
  if (response.preparedDate != null) metadata.preparedDate = response.preparedDate;
  if (response.reviewedDate != null) metadata.reviewedDate = response.reviewedDate;
  if (response.preparedUserIds != null) metadata.preparedUserIds = response.preparedUserIds;
  if (response.reviewedUserIds != null) metadata.reviewedUserIds = response.reviewedUserIds;

  const hasMetadata = Object.keys(metadata).length > 0;
  const finish = (rows: WorkingPaperData[]): WorkingPaperData[] => {
    const companyId = opts?.companyId ?? 0;
    const annualId = opts?.annualId ?? 0;
    const code = opts?.code || '';
    if (companyId > 0 && annualId > 0 && code && rows[0]) {
      hydrateWpSignoffFromPayload(
        { companyId, annualId, code, yearOfAssessment: opts?.activeYoa || '' },
        rows[0] as Record<string, unknown>,
      );
    }
    return rows;
  };

  if (details.length === 0) {
    return hasMetadata ? finish([metadata as WorkingPaperData]) : [];
  }

  const first = details[0];
  if (first != null && typeof first === 'object' && !Array.isArray(first)) {
    const merged = { ...(first as Record<string, unknown>), ...metadata };
    return finish([merged as WorkingPaperData, ...(details.slice(1) as WorkingPaperData[])]);
  }

  const rows = hasMetadata
    ? [metadata as WorkingPaperData, ...(details as WorkingPaperData[])]
    : (details as WorkingPaperData[]);
  return finish(rows);
}
